import { useCallback, useEffect, useState } from "react";

import { AssistitoRepository, GeneralRepository } from "core/data/index";

import { IScegliAssistitoProps } from "pages/Prenotazione/Form/Assistito/ScegliAssistito/IScegliAssistitoProps";
import { IFamiliare } from "core/interfaces/data/IAssistitoRequest";

import { Alert, Form, Input, Select, Skeleton, Spin } from "antd";
import { CustomButton, CardFamiliare, HrText, RemoteSelect } from "core/interfaces/components/index";

import "./ScegliAssistito.css";
import { customNotification } from "components/CustomNotification/CustomNotification";

import { performAsyncFunction } from "utils/AsyncFunctionUtility";
import { CustomButtonType } from "components/CustomButton/CustomButtonType";

import Riepilogo from "components/Riepilogo/Riepilogo";
import { IOption } from "core/interfaces/data/IOption";
import { IComune, IMunicipio } from "core/interfaces/data/IGeneralRequest";
import _ from "lodash";
import { notifyValidationError } from "components/NotificaValidazioneFallita/NotificaValidazioneFallita";
import { setIndirizzo, setLuogoErogazione } from "core/redux/features/dataForm/dataFormSlice";
import { ILuogoErogazione } from "core/interfaces/data/ILuogoErogazione";
import { Enhanced } from "utils/ReactUtils";

interface IFormData {
	Comune: IOption;
	CapComune: string;
	IndirizzoDettagli: string;
	Indirizzo: string;
}
const { Option } = Select;
const ScegliAssistito: React.FC<IScegliAssistitoProps> = ({
	OnClickInserisciFamiliare,
	OnSubmitFamiliareEsistente,
	OnClickInserisciRichiedente,
	dataForm,
	dispatch,
	i18n,
	sessionInfo,
	t,
	handleError,
}) => {
	const [familiareSelected, setFamiliareSelected] = useState<IFamiliare | undefined>(undefined);
	const [loading, setLoading] = useState<boolean>(true);
	const [familiari, setFamiliari] = useState<IFamiliare[] | undefined>(undefined);
	const [spinning, setSpinning] = useState<boolean>(false);
	const [form] = Form.useForm();

	const [comune, setComune] = useState<IOption>();

	const [municipi, setMunicipi] = useState<IMunicipio[]>([]);
	const [needMunicipio, setNeedMunicipio] = useState<boolean>(false);

	const [comuneDomicilioLocked, setComuneDomicilioLocked] = useState<boolean>(false);
	const [comuneDomicilioDefaultDataSource, setComuneDomicilioDefaultDataSource] = useState<IOption[]>();

	const getFamiliari = useCallback(async () => {
		if (sessionInfo.isLogged) {
			try {
				const familiari = await AssistitoRepository.GetFamiliari({
					IdSessione: sessionInfo.sessione!.idSessione,
					culture: i18n.language,
				});

				return familiari;
			} catch (err: any) {
				return [];
			}
		}
	}, [i18n.language, sessionInfo]);

	const getComuni = useCallback(
		async (comune: string, valido?: boolean, codiceIstat?: string, cap?: string): Promise<IComune[] | undefined> => {
			const comuni = await GeneralRepository.getComuni({
				Comune: comune,
				CodiceIstat: codiceIstat,
				Cap: cap,
				culture: i18n.language,
				Validi: valido,
			});

			return comuni;
		},
		[i18n.language]
	);

	const comuneOption = (comune: IComune, index: number): IOption => {
		let res: IOption = { label: `${comune.Descrizione} (${comune.Provincia})`, value: comune.CodIstat, key: index };
		return res;
	};

	useEffect(() => {
		performAsyncFunction(async () => {
			setLoading(true);
			const familiari = await getFamiliari();
			setFamiliari(familiari);
			var luogoErogazione = dataForm.area?.LuogoErogazione;
			if (luogoErogazione) {
				var comuni: IComune[] | undefined;

				comuni = await getComuni(luogoErogazione.Comune, true, luogoErogazione?.CodIstat, luogoErogazione?.CAP);

				setComuneDomicilioDefaultDataSource(
					comuni?.map((c) => {
						return comuneOption(c, c.ID);
					})
				);
				if (comuni) {
					if (comuni?.length === 1) {
						setComuneDomicilioLocked(true);
						var comune = comuni[0];
						var comOption = comuneOption(comune, comune.ID);
						setComune(comOption);
						form.setFieldsValue({
							Comune: comOption,
							Indirizzo: luogoErogazione.Via ? `${luogoErogazione.Via} ${luogoErogazione.Civico ?? ""}` : undefined,
							CapComune: luogoErogazione.CAP,
						} as IFormData);
					} else {
						setComuneDomicilioLocked(false);
						form.setFieldsValue({
							CapComune: luogoErogazione.CAP,
						} as IFormData);
					}
				}
			}
			setLoading(false);
		}, handleError);
	}, [dataForm.area?.LuogoErogazione, form, getComuni, getFamiliari, handleError]);

	const OnClickCard = (id: any) => {
		if (familiareSelected?.Matricola === id) {
			setFamiliareSelected(undefined);
		} else {
			var familiare = familiari?.filter((el) => parseInt(el.Matricola) === parseInt(id))[0];
			setFamiliareSelected(familiare);

			if (familiare?.CapDomicilio === form.getFieldValue("CapComune")) {
				var comuneFamiliare = comuneDomicilioDefaultDataSource?.find((c) => {
					return c.value === familiare?.IstatDomicilio;
				});
				if (comuneFamiliare) {
					setComune(comuneFamiliare);
					form.setFieldsValue({
						Comune: comuneFamiliare,
					});
					setComuneDomicilioLocked(true);
				}

				form.setFieldsValue({
					Indirizzo: familiare?.IndirizzoDomicilio ?? "",
					IndirizzoDettagli: familiare?.IndirizzoDomicilioDett ?? "",
				} as IFormData);
			} else {
				setComune(undefined);
				setComuneDomicilioLocked(false);
				form.setFieldsValue({ Indirizzo: "", IndirizzoDettagli: "" } as IFormData);
			}
		}
	};

	const OnSubmitFamiliare = () => {
		if (familiareSelected) {
			setSpinning(true);

			OnSubmitFamiliareEsistente(familiareSelected);
		} else {
			customNotification(t("scegli.familiareNotSelected.title"), t("scegli.familiareNotSelected.text"));
		}
	};

	const fetchComuni = async (comune: string): Promise<IOption[]> => {
		var cap = dataForm.area?.LuogoErogazione?.CAP;

		const comuni = await getComuni(comune, undefined, undefined, cap);

		if (comuni) {
			return comuni.map((comune, index) => ({ label: `${comune.Descrizione} (${comune.Provincia})`, value: comune.CodIstat, key: index }));
		}

		return [];
	};
	const familiareSortLogic = (x: IFamiliare, y: IFamiliare) => {
		// comparo il nome e il cognome permetterli in ordine
		var nameOrder: number = 0;
		if (x.Cognome < y.Cognome) {
			nameOrder = -1;
		} else if (x.Cognome > y.Cognome) {
			nameOrder = 1;
		} else if (x.Nome > y.Nome) {
			nameOrder = -1;
		} else if (x.Nome > y.Nome) {
			nameOrder = 1;
		}

		return x.UtenteIsRichiedente ? -1 : nameOrder;
	};

	const getMunicipi = async (codIstat: string): Promise<IMunicipio[] | undefined> => {
		const municipiFetched = await GeneralRepository.getMunicipi({
			CodiceIstat: codIstat,
			culture: i18n.language,
		});

		return municipiFetched;
	};

	const OnFinishFailed = (e: any) => {
		const input = document.querySelector(`#${e.errorFields[0].name[0]}`);

		if (input) {
			input.scrollIntoView({
				behavior: "smooth",
				block: "start",
				inline: "start",
			});
		}

		const errors = e.errorFields;

		let notifica = {
			Title: t("assistito.requiredMessages.notifica.titolo"),
			Message: new Array<string>(),
		};
		_.each(errors, (err, index: number) => {
			notifica.Message.push(t("assistito.requiredMessages.notifica." + err.name[0]));
		});

		notifyValidationError(notifica.Title, notifica.Message);
	};

	const OnSubmitForm = async (data: IFormData) => {
		setSpinning(true);
		let via = `${data.Indirizzo} ${data.IndirizzoDettagli}`;
		// if (!dataForm.area?.LuogoErogazione?.Via) {
		dispatch(setIndirizzo(via));
		// }
		if (!dataForm.area?.LuogoErogazione?.CodIstat && dataForm.area?.LuogoErogazione) {
			const comuneSelected: IOption = data.Comune ;
			var comuni = await getComuni(
				comuneSelected.label.substring(0, comuneSelected.label.indexOf(" (")),
				true,
				comuneSelected.value.toString(),
				data.CapComune
			);
			if (comuni && comuni?.length === 1) {
				var c = comuni[0];
				var luogoErigazione: ILuogoErogazione = {
					CAP: dataForm.area!.LuogoErogazione!.CAP,
					Civico: dataForm.area!.LuogoErogazione!.Civico,
					CAPMultipli: dataForm.area!.LuogoErogazione!.CAPMultipli,
					Via: via,
					Comune: c.Descrizione,
					Provincia: c.Provincia,
					CodIstat: c.CodIstat,
				};
				dispatch(setLuogoErogazione(luogoErigazione));
			}
		}
		OnSubmitFamiliare();
	};

	const OnSelectComuneDomicilio = async (value: any) => {
		form.setFieldsValue({
			CapComuneDomicilio: "",
		});

		setComune(value);
		setNeedMunicipio(false);

		if (value) {
			const comuni = await getComuni(
				value.label.substring(0, value.label.indexOf(" (")),
				true,
				value.value.toString(),
				dataForm.area?.LuogoErogazione?.CAP
			);

			if (comuni) {
				const comune = comuni.filter((comune) => comune.CodIstat === value.value)[0];

				if (comune.CAP === null || comune.CAP === "") {
					form.setFieldsValue({
						CapComune: dataForm.area?.LuogoErogazione?.CAP,
					} as IFormData);
				} else {
					form.setFieldsValue({
						CapComune: comune.CAP,
					} as IFormData);
				}

				const municipiComune = await getMunicipi(comune.CodIstat);

				if (municipiComune && municipiComune.length !== 0) {
					setNeedMunicipio(true);
					setMunicipi(municipiComune);
				}
			}
		}
	};

	return (
		<div id="scegli-assistito">
			<div className="title">
				<span>{t("scegli.title")}</span>
			</div>
			<Riepilogo />
			{loading ? (
				<Skeleton active />
			) : (
				<>
					{familiari ? (
						<>
							<Spin spinning={spinning}>
								<div className="spinner-area">
									<div className="familiari-list">
										<>
											<HrText text={t("scegli.richiedente.title")} />
											{familiari.filter((x) => x.UtenteIsRichiedente).length > 0 ? (
												<>
													{familiari
														.filter((x) => x.UtenteIsRichiedente)
														.map((familiare, index) => {
															return (
																<div
																	className={`card-container ${familiare.UtenteIsRichiedente ? "user-is-assisted" : ""} margin-bottom-5`}
																	key={familiare.Matricola}
																>
																	<CardFamiliare
																		familiare={familiare}
																		OnClick={OnClickCard}
																		selected={familiareSelected}
																		key={index}
																		showAddressDetails={false}
																		showLegame={false}
																	/>
																</div>
															);
														})}
												</>
											) : (
												<CustomButton
													text={t("scegli.richiedente.insert")}
													className={"margin-bottom-5"}
													style={{ width: "100%" }}
													OnClick={OnClickInserisciRichiedente}
													buttonType={CustomButtonType.positive}
												/>
											)}

											{familiari.filter((x) => !x.UtenteIsRichiedente).length > 0 && (
												<>
													<HrText text={t("scegli.familiare")} />
													{familiari
														.filter((x) => !x.UtenteIsRichiedente)
														.sort(familiareSortLogic)
														.map((familiare, index) => {
															return (
																<div
																	className={`card-container ${familiare.UtenteIsRichiedente ? "user-is-assisted" : ""}`}
																	key={familiare.Matricola}
																>
																	<CardFamiliare
																		familiare={familiare}
																		OnClick={OnClickCard}
																		selected={familiareSelected}
																		key={index}
																		showAddressDetails={false}
																	/>
																</div>
															);
														})}
												</>
											)}
										</>
									</div>
									<div className={"form-familiare " + (familiareSelected !== undefined ? "form-familiare-show" : "")}>
										<Form id="formAssistito" onFinishFailed={OnFinishFailed} onFinish={OnSubmitForm} form={form} initialValues={{ remember: true }}>
											<>
												<HrText text={" *" + t("assistito.text.indirizzoAssistenza")} />{" "}
												<div className="indirizzo-assistito">
													<div>
														<Form.Item
															name="Indirizzo"
															label={t("assistito.form.addressHome.address.name")}
															labelAlign={"left"}
															rules={[{ required: true, message: t("assistito.requiredMessages.address") }]}
															labelCol={{ span: 9 }}
															colon
														>
															<Input placeholder={t("assistito.form.addressHome.address.placeholder")} />
														</Form.Item>
													</div>
													<div>
														<Form.Item
															label={t("assistito.form.addressHome.home.name")}
															name="IndirizzoDettagli"
															labelAlign={"left"}
															colon
															labelCol={{ span: 9 }}
														>
															<Input placeholder={t("assistito.form.addressHome.home.placeholder")} />
														</Form.Item>
													</div>
													<div>
														<Form.Item
															name="Comune"
															label={t("assistito.form.addressHome.municipality.name")}
															rules={[{ required: true, message: t("assistito.requiredMessages.residenceMunicipality") }]}
															labelAlign={"left"}
															colon
															labelCol={{ span: 9 }}
														>
															<RemoteSelect
																allowClear
																disabled={comuneDomicilioLocked}
																placeholder={t("assistito.form.addressHome.municipality.placeholder")}
																debounceTimeout={10}
																fetchOptions={fetchComuni}
																value={comune?.value}
																searchValue={comune?.label}
																OnChange={(newValue, option: any) => {
																	form.setFieldsValue({
																		Comune: option,
																	});
																	OnSelectComuneDomicilio(option);
																}}
																defaultDatasource={comuneDomicilioDefaultDataSource}
															/>
														</Form.Item>
													</div>
													<div>
														<Form.Item
															name="CapComune"
															label={t("assistito.form.CAP.Domicilio")}
															rules={[{ required: true, message: t("assistito.requiredMessages.CAP") }]}
															labelAlign={"left"}
															labelCol={{ span: 9 }}
															colon
														>
															<Input disabled={true} placeholder={t("assistito.form.CAP.Domicilio")} />
														</Form.Item>
													</div>
													{needMunicipio && (
														<div>
															<Form.Item
																name="MunicipioDomicilio"
																label={t("assistito.form.address.hamlet.name")}
																rules={[{ required: true, message: t("assistito.requiredMessages.townHall") }]}
																labelCol={{ span: 8 }}
																colon
															>
																<Select placeholder={t("assistito.form.addressHome.hamlet.placeholder")}>
																	{municipi.map((municipio, index) => {
																		return (
																			<Option key={index} value={municipio.Value}>
																				{municipio.Text}
																			</Option>
																		);
																	})}
																</Select>
															</Form.Item>
														</div>
													)}
												</div>
											</>
										</Form>
									</div>

									<CustomButton
										text={t("scegli.conferma")}
										className={"margin-bottom-5"}
										OnClick={() => {
											form.submit();
										}}
										buttonType={CustomButtonType.positive}
										enabled={familiareSelected !== undefined}
									/>

									<HrText text={t("scegli.insertFamiliare.title")} />
									<CustomButton
										text={t("scegli.insertFamiliare.button")}
										className={"margin-bottom-5"}
										OnClick={() => OnClickInserisciFamiliare()}
										buttonType={CustomButtonType.positive}
									/>
								</div>
							</Spin>
						</>
					) : (
						<Alert message={t("errorMessages.base")} type="error" showIcon banner />
					)}
				</>
			)}
		</div>
	);
};

export default Enhanced(ScegliAssistito, { componentNamespace: "ScegliAssistito" });
